import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import {
  parsePhoneNumberFromString,
  getCountryCallingCode,
  getCountries,
} from "libphonenumber-js";
import ReactPhoneInput from "react-phone-input-2";
import { useSelector } from "react-redux";
// css
import "./Updateuser.css";
import "react-phone-input-2/lib/style.css";
// custom imports
import UserManagement from "../../../Services/UserManagement/UserManagementService";
import { HandleApiError } from "../../../Utils/CommonErrors/HandleApiError";
import showToast from "../../../Utils/Toast/ToastNotification";
import {
  showLoader,
  hideLoader,
} from "../../../Redux/Slice/Loader/loaderSlice";

function getCountryISO(countryCode) {
  return getCountries().find(
    (iso) => getCountryCallingCode(iso) === countryCode.replace("+", "")
  );
}
const Updateuser = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  const [userDetails, setUserDetails] = useState({
    first_name: "",
    last_name: "",
    email: "",
    role_id: "",
    phone_number: "",
    is_active: "",
    country_code: "", // this will store country code with "+" sign

  });

  const [roles, setRoles] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = useSelector((state) => state.auth?.user?.data?.body?.data?.token)

  const getUserDetails = async () => {
    try {
      dispatch(showLoader());

      const user = await UserManagement.getUser(id, token);
      const userData = user?.data?.body?.data.users[0];

      // Set user details with response data, ensure country_code retains "+" sign
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        ...userData,
        role_id: userData.role?.id || "",
      }));
    } catch (error) {
      await HandleApiError(error);
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    getUserDetails();
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      const response = await UserManagement.getRole(token);
      if (response?.data?.statusCode === 200) {
        setRoles(response?.data?.body?.data?.roles);
      }
    };
    fetchRoles();
  }, []);

  const validate = (values) => {
    const validationErrors = {};
    const schema = Yup.object({
      first_name: Yup.string().required("First Name is required"),
      last_name: Yup.string().required("Last Name is required"),
      role_id: Yup.string().required("Role is required"),
      email: Yup.string().email("Invalid email address").nullable(),
      phone_number: Yup.string().test(
        "phone-number",
        "Invalid phone number",
        function (value) {
          if (!value) {
            return true; // Allow empty field
          }

          const { country_code } = this.parent;
          if (!country_code || !value) {
            return this.createError({
              message: `Country code and phone number are required.`,
            });
          }
          let formattedNumber = `${country_code}${value.replace(
            /^(\+)?(\d+)/,
            "$2"
          )}`;

          const countryISO = getCountryISO(country_code);
          if (!countryISO) {
            return this.createError({
              message: `Invalid country code: ${country_code}`,
            });
          }

          // Clean the phone number by removing all non-numeric characters (except '+')
          const cleanedValue = value.replace(/[^0-9+]/g, "");

          // Validate phone number using libphonenumber-js
          const parsedNumber = parsePhoneNumberFromString(formattedNumber);

          if (!parsedNumber || !parsedNumber.isValid()) {
            return this.createError({
              message: `Invalid phone number for ${country_code}`,
            });
          }

          // Extract the correct national number
          const phoneNumberWithoutCountryCode = parsedNumber.nationalNumber;


          // Store cleaned values
          this.parent.phone_number = phoneNumberWithoutCountryCode;
          this.parent.country_code = country_code;

          return true;
        }
      ),
    });

    try {
      schema.validateSync(values, { abortEarly: false });
    } catch (err) {
      err.inner.forEach((e) => {
        validationErrors[e.path] = e.message;
      });
    }

    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const validationErrors = validate(userDetails);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await UserManagement.updateUser(id, userDetails, token);
      showToast("success", response?.data?.body?.message);
      navigate("/user-listing");
    } catch (error) {
      await HandleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const handlePhoneChange = (value, data) => {
    const countryCode = `+${data.dialCode}`; // Ensure the country code has '+'
    let numberWithoutCode = value.replace(countryCode, "").trim();
    if (numberWithoutCode.startsWith("1") && data.dialCode !== "1") {
      numberWithoutCode = numberWithoutCode.slice(1);
    }

    setUserDetails((prevUser) => ({
      ...prevUser,
      phone_number: numberWithoutCode, // Store the number without country code
      country_code: countryCode, // Store the selected country code
    }));
  };

  return (
    <>
      <div className="main-inner-block">
        <div className="heading-content">Edit User</div>
        <div className="content">
          <div className="white-box">
            <form onSubmit={handleSubmit}>
              <div className="d-flex gap-3 create-user-block w-100 justify-content-between">
                <div className="row m-0 w-100">
                  <div className="mb-3 col-lg-3 ps-0 padding-right">
                    <label htmlFor="firstName" className="form-label">
                      First Name<span>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="firstName"
                      name="first_name"
                      placeholder="Enter first name"
                      value={userDetails.first_name}
                      onChange={handleChange}
                    />
                    {errors.first_name && (
                      <div className="error">{errors.first_name}</div>
                    )}
                  </div>

                  <div className="mb-3 col-lg-3 padding-left">
                    <label htmlFor="lastName" className="form-label">
                      Last Name<span>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="lastName"
                      name="last_name"
                      placeholder="Enter last name"
                      value={userDetails.last_name}
                      onChange={handleChange}
                    />
                    {errors.last_name && (
                      <div className="error">{errors.last_name}</div>
                    )}
                  </div>

                  <div className="mb-3 col-lg-3 padding-left">
                    <label htmlFor="email" className="form-label">
                      Email
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      name="email"
                      placeholder="Enter Email"
                      value={userDetails.email}
                      onChange={handleChange}
                    />
                    {errors.email && (
                      <div className="error">{errors.email}</div>
                    )}
                  </div>


                  <div className="mb-3 col-lg-3 ps-0 padding-right">
                    <label htmlFor="role" className="form-label">
                      Assign Role
                    </label>
                    <select
                      className="form-select"
                      id="role"
                      name="role_id"
                      value={userDetails.role_id}
                      onChange={handleChange}
                    >
                      <option value="" disabled>Select role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                    {errors.role_id && (
                      <div className="error">{errors.role_id}</div>
                    )}
                  </div>

                  <div className="mb-3 col-lg-3 ps-0 padding-right">
                    <label htmlFor="is_active" className="form-label">
                      Status
                    </label>
                    <select
                      className="form-select"
                      id="is_active"
                      name="is_active"
                      value={userDetails.is_active}
                      onChange={handleChange}
                    >
                      <option value="" disabled>Select status</option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>

                  <div className="mb-3 col-lg-3 padding-left country-code-input">
                    <label htmlFor="country_code" className="form-label">
                      Country Code
                    </label>
                    <ReactPhoneInput
                      country={"au"}
                      value={
                        userDetails.country_code + userDetails.phone_number
                      } // Show +91 + number
                      onChange={(value, country) => {
                        // Ensure only one +91 is there
                        let cleanedValue = value.replace(
                          new RegExp(`^\\+?${country.dialCode}`),
                          ""
                        );
                        setUserDetails({
                          ...userDetails,
                          phone_number: cleanedValue, // Save only the number without +91
                          country_code: `+${country.dialCode}`, // Save correct country code
                        });
                      }}
                      inputProps={{
                        name: "phone_number",
                        autoFocus: false,
                      }}
                    />
                    {errors.phone_number && (
                      <div className="error">{errors.phone_number}</div>
                    )}
                  </div>




                </div>
              </div>
              <div className="text-center d-flex justify-content-center button-block gap-3 mt-3">
                <button
                  type="button"
                  className="green-btn submit-btn"
                  onClick={() => navigate("/user-listing")}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="green-btn submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Updateuser;
